import dayjs from "dayjs";

import { AuthorTypeEnum } from "@fitness-app/data-models/entities/AuthorTypeEnum";
import {
  BodyMeasurement,
  type BodyMeasurementData,
  type HealthStepsMeasurementData,
  type MeasurementType,
} from "@fitness-app/data-models/entities/Measurement";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { supabase } from "~/store/initializeStore";
import { logSimulatorWrite, SIMULATOR_TABLE } from "./simulatorWriteLog";

/**
 * Typy pomiarów, które klientka wprowadza sama i które wpadają do `client_activity`
 * (a stamtąd do kategorii `weightMeasurements`, `bodyMeasurements` i `hydration` w wyzwaniach).
 */
/** Etykiety do rejestru — surowe wartości enuma („weight_measurement") są nieczytelne w UI. */
const MEASUREMENT_LABEL: Record<string, string> = {
  weight_measurement: "Waga",
  body_measurements: "Obwody",
  hydration_measurement: "Nawodnienie",
  health_steps_measurement: "Kroki",
  feedback_about_training: "Ocena treningu",
};

export const measurementLabel = (type: string) => MEASUREMENT_LABEL[type] ?? type;

export type SimulatorMeasurementType =
  | MeasurementType.WeightMeasurement
  | MeasurementType.BodyMeasurements
  | MeasurementType.HydrationMeasurement
  | MeasurementType.HealthStepsMeasurement;

type WriteParams = {
  traineeId: string;
  userId: string | null;
  createdBy: string;
  date: string;
  mode: "single" | "bulk";
  type: SimulatorMeasurementType;
  data: { weight: number } | BodyMeasurementData | { portion: number } | HealthStepsMeasurementData;
};

/**
 * Kroki normalnie wpadają z Health Connect / Apple Health, więc payload musi mieć komplet pól,
 * których oczekuje reszta aplikacji — `source: "mobile"` i okno czasowe doby, a nie sam licznik.
 */
export const buildStepsData = (steps: number, date: string): HealthStepsMeasurementData => {
  const day = dayjs(date, "YYYY-MM-DD");
  return {
    provider: "health-connect",
    source: "mobile",
    steps,
    startDate: day.startOf("day").toISOString(),
    endDate: day.endOf("day").toISOString(),
    syncedAt: new Date().toISOString(),
  };
};

/**
 * Zapisuje pomiar tak, jakby zrobiła to klientka (`creator: Client` — inaczej raporty
 * i statystyki potraktują go jako wpis trenerki).
 *
 * Kolejność „najpierw update, potem insert" jest wymuszona przez częściowy indeks
 * `measurement_daily_type_unique_idx` na (traineeId, type, eventDate): drugi pomiar tego
 * samego typu w tym samym dniu nie jest nowym wierszem, tylko nadpisaniem istniejącego.
 * Zwykły `upsert` z `onConflict` nie zadziała, bo indeks ma predykat.
 */
export const writeMeasurement = async ({
  traineeId,
  userId,
  createdBy,
  date,
  mode,
  type,
  data,
}: WriteParams): Promise<string> => {
  const now = new Date().toISOString();

  // Snapshot sprzed nadpisania: jeśli pomiar już istniał, to dane klientki i czyszczenie
  // symulatora musi je przywrócić, a nie skasować wiersz.
  const { data: existingBefore, error: existingError } = await supabase
    .from("measurement")
    .select("id, data")
    .match({ traineeId, type, eventDate: date })
    .maybeSingle();

  // Bez snapshotu nie wolno iść dalej: `measurement.data` jest NOT NULL, więc zapisanie
  // `before: { data: null }` wywróciłoby całe czyszczenie na tym jednym wierszu.
  if (existingError) {
    throw new Error(`Nie udało się odczytać poprzedniego pomiaru: ${existingError.message}`);
  }

  const { data: updated, error: updateError } = await supabase
    .from("measurement")
    .update({ data, updatedAt: now } as never)
    .match({ traineeId, type, eventDate: date })
    .select("id");

  if (updateError) {
    throw new Error(updateError.message);
  }

  const existingId = updated?.[0]?.id;

  if (existingId) {
    // Wiersz pojawił się między odczytem a zapisem (równoległy wpis klientki) — nie mamy snapshotu,
    // więc nie da się uczciwie zaklasyfikować tej zmiany. Lepiej przerwać niż zapisać wpis
    // rejestru, który przy czyszczeniu skasuje cudze dane.
    if (!existingBefore) {
      throw new Error("Pomiar zmienił się w trakcie zapisu — spróbuj ponownie");
    }

    await logSimulatorWrite({
      traineeId,
      createdBy,
      tableName: SIMULATOR_TABLE.measurement,
      rowId: existingId,
      eventDate: date,
      mode,
      op: "update",
      before: { data: existingBefore.data },
      label: measurementLabel(type),
    });
    return existingId;
  }

  const { data: inserted, error } = await supabase
    .from("measurement")
    .insert({
      id: generateUniqId(),
      type,
      data,
      eventDate: date,
      eventTimestamp: dayjs(date, "YYYY-MM-DD").hour(12).toISOString(),
      traineeId,
      userId,
      creator: AuthorTypeEnum.Client,
      createdAt: now,
      updatedAt: now,
    } as never)
    .select("id")
    .single();

  if (error) {
    throw new Error(error.message);
  }

  await logSimulatorWrite({
    traineeId,
    createdBy,
    tableName: SIMULATOR_TABLE.measurement,
    rowId: inserted.id,
    eventDate: date,
    mode,
    op: "insert",
    label: measurementLabel(type),
  });

  return inserted.id;
};

/**
 * Obwody wypełniane w symulatorze. Pełen zestaw byłby nieczytelny w formularzu, a do
 * `client_activity` i tak trafia sam fakt wpisania pomiaru, nie liczba wypełnionych pól.
 */
export const SIMULATOR_BODY_MEASUREMENT_KEYS = [
  BodyMeasurement.WAIST,
  BodyMeasurement.HIPS,
  BodyMeasurement.CHEST,
  BodyMeasurement.THIGH_LEFT,
] as const;

export const buildBodyMeasurementData = (
  values: Partial<Record<BodyMeasurement, number | null>>,
): BodyMeasurementData => values as BodyMeasurementData;
